import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import EventDetailsV2Context from '@totem/components/services/threats/eventsDashboard/details/EventDetailsV2Context';
import { EventDetail } from '@totem/components/services/threats/eventsDashboard/details/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import {
  EVENTS_ENDPOINT,
  V2_EVENTS_ENDPOINT,
} from '@totem/utilities/endpoints';

// The v1 ticket endpoints answer with an events list for these parameters; one row keeps that response small.
const TICKET_RESPONSE_PARAMS: Params = {
  paging: { page: 1, pageSize: 1 },
  sort: { field: 'eventTime', direction: -1 },
  filters: [],
};

type Props = {
  eventId: string;
  children?: ReactNode;
};

const EventDetailsV2Container = ({ eventId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [event, setEvent] = useState<EventDetail>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);

  const headers = () =>
    new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getApiToken()}`,
    });

  useEffect(() => {
    setLoading(true);

    setNotFound(false);

    fetch(`${V2_EVENTS_ENDPOINT}/dashboard/${eventId}`, { headers: headers() })
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true);
          return null;
        }
        if (!res.ok) {
          throw new Error(`${res.status}`);
        }
        return res.json();
      })
      .then((result: EventDetail) => setEvent(result))
      .catch(() =>
        notification.error({
          title: 'Event',
          description: 'The event could not be loaded.',
        }),
      )
      .finally(() => setLoading(false));
  }, [eventId, refreshCount]);

  const send = (request: Promise<Response>, failure: string) => {
    setSending(true);

    request
      .then((res) => {
        if (!res.ok) {
          throw new Error(`${res.status}`);
        }
      })
      .then(() => setRefreshCount((count) => count + 1))
      .catch(() => notification.error({ title: 'Event', description: failure }))
      .finally(() => setSending(false));
  };

  const setStatus = (status: 'ACKNOWLEDGED' | 'CLEARED') =>
    send(
      fetch(`${V2_EVENTS_ENDPOINT}/dashboard/${eventId}/status`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ status }),
      }),
      'The event status could not be updated.',
    );

  const createTicket = () =>
    send(
      fetch(`${EVENTS_ENDPOINT}/${eventId}/createTicket`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(TICKET_RESPONSE_PARAMS),
      }),
      'The ticket could not be created.',
    );

  const associateTicket = (ticketId: string, ticketNumber: string) =>
    send(
      fetch(`${EVENTS_ENDPOINT}/${eventId}/associateTicket`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          ticketID: ticketId,
          ticketNumber,
          query: TICKET_RESPONSE_PARAMS,
        }),
      }),
      'The ticket could not be linked.',
    );

  return (
    <EventDetailsV2Context.Provider
      value={{
        event,
        loading,
        notFound,
        sending,
        setStatus,
        createTicket,
        associateTicket,
      }}
    >
      {children}
    </EventDetailsV2Context.Provider>
  );
};

export default EventDetailsV2Container;
